import type { TablePaginationConfig } from 'antd';
import type { FilterValue, SortOrder } from 'antd/es/table/interface';
import type { Key } from 'react';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';

import i18n from '@/i18n';

/**
 * Состояние таблицы — в URL: ?page=2&size=50&sort=-created_at&status=draft&status=shipped.
 * Ссылку на отфильтрованный список можно переслать, «назад» в браузере работает,
 * перезагрузка страницы ничего не теряет.
 */
export interface TableStateOptions<F extends string> {
  /** Имена фильтров — они же параметры запроса к API. */
  filters?: readonly F[];
  defaultSort?: string;
  defaultPageSize?: number;
}

/** Подмножество SorterResult<T>: подходит таблице с любым типом строк. */
interface SorterLike {
  columnKey?: Key | undefined;
  order?: SortOrder | undefined;
}

export interface TableQuery {
  limit: number;
  offset: number;
  sort?: string;
}

const PAGE_SIZES = [20, 50, 100, 200];

function positiveInt(raw: string | null, fallback: number): number {
  const n = raw === null ? NaN : Number.parseInt(raw, 10);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

export function useTableState<F extends string = never>({
  filters = [],
  defaultSort,
  defaultPageSize = 50,
}: TableStateOptions<F> = {}) {
  const [params, setParams] = useSearchParams();

  const page = positiveInt(params.get('page'), 1);
  const size = positiveInt(params.get('size'), defaultPageSize);
  const pageSize = PAGE_SIZES.includes(size) ? size : defaultPageSize;
  const sort = params.get('sort') ?? defaultSort;

  const filterValues = useMemo(() => {
    const out = {} as Record<F, string[]>;
    for (const name of filters) out[name] = params.getAll(name);
    return out;
    // filters — константа вызывающего; сравнение по содержимому дороже пользы
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  /** Сменить фильтр; страница — снова первая, иначе можно оказаться за концом списка. */
  const setFilter = useCallback(
    (name: F, values: readonly string[] | string | null) => {
      update((next) => {
        next.delete(name);
        const list = values === null ? [] : typeof values === 'string' ? [values] : values;
        for (const v of list) if (v !== '') next.append(name, v);
        next.delete('page');
      });
    },
    [update],
  );

  const reset = useCallback(() => {
    update((next) => {
      for (const name of [...filters, 'page', 'sort']) next.delete(name);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [update]);

  /** Обработчик Table.onChange: пагинация, сортировка, фильтры колонок. */
  const onTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      tableFilters: Record<string, FilterValue | null>,
      sorter: SorterLike | SorterLike[],
    ) => {
      update((next) => {
        const s = Array.isArray(sorter) ? sorter[0] : sorter;
        const key = s?.columnKey === undefined ? undefined : String(s.columnKey);
        const nextSort = key && s?.order ? `${s.order === 'descend' ? '-' : ''}${key}` : undefined;
        const nextSize = pagination.pageSize ?? pageSize;
        const sortChanged = (nextSort ?? defaultSort) !== sort;
        if (nextSort) next.set('sort', nextSort);
        else next.delete('sort');
        if (nextSize === defaultPageSize) next.delete('size');
        else next.set('size', String(nextSize));

        let filtersChanged = false;
        for (const name of filters) {
          if (!(name in tableFilters)) continue; // фильтр не в колонке — управляется снаружи
          const values = (tableFilters[name] ?? []).map(String);
          const before = next.getAll(name);
          if (values.join('\u0000') !== before.join('\u0000')) filtersChanged = true;
          next.delete(name);
          for (const v of values) next.append(name, v);
        }
        const nextPage =
          sortChanged || filtersChanged || nextSize !== pageSize ? 1 : (pagination.current ?? 1);
        if (nextPage === 1) next.delete('page');
        else next.set('page', String(nextPage));
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [update, pageSize, sort, defaultSort, defaultPageSize],
  );

  /** Для колонки: текущее направление сортировки (колонка управляемая). */
  const sortOrderFor = useCallback(
    (key: string): SortOrder => {
      if (sort === key) return 'ascend';
      if (sort === `-${key}`) return 'descend';
      return null;
    },
    [sort],
  );

  /** Параметры для запроса к API (limit/offset, sort) — фильтры добавляет вызывающий. */
  const query: TableQuery = {
    limit: pageSize,
    offset: (page - 1) * pageSize,
    ...(sort ? { sort } : {}),
  };

  const pagination = useCallback(
    (total: number | undefined): TablePaginationConfig => ({
      current: page,
      pageSize,
      total: total ?? 0,
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZES,
      showTotal: (n) => i18n.t('table.total', { count: n }),
    }),
    [page, pageSize],
  );

  return {
    page,
    pageSize,
    sort,
    filters: filterValues,
    query,
    setFilter,
    reset,
    onTableChange,
    sortOrderFor,
    pagination,
  };
}
